import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { Input, version2 } from 'ui-components/components/input';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { RadioCheckList } from 'ui-components/components/radio-check-list';
import { RadioCheckListItem } from 'ui-components/components/radio-check-list/radio-check-list-item';
import { Text, TextType } from 'ui-components/components/text';

import { CountryCodeSelect } from './country-code-select';
import { AUTH0_MFA_TYPES, PHONE_VERIFICATION_STATES } from '../../../../../../../authentication/constants';
import { SimpleDropdown } from '../../../../../../../components/simple-dropdown';
import { MANAGEMENT_TYPES } from '../../../../../../../global/step-up-authentication/constants';
import { messages } from '../../messages';
import style from '../add-mfa-enrollment.module.less';
import { EnrollmentErrorBanner } from '../enrollment-error-banner';

const VerificationMethods = [
  {
    key: AUTH0_MFA_TYPES.sms,
    name: <FormattedMessage {...messages.smsEnrollmentOption} />,
  },
  {
    key: AUTH0_MFA_TYPES.voice,
    name: <FormattedMessage {...messages.voiceEnrollmentOption} />,
  },
];

export const PhoneEnrollmentForm = ({
  countryCode,
  currentMfaType,
  enrollmentValue,
  error,
  isUnskippable = false,
  onCancel,
  onStartVerification,
  phoneVerificationState,
  resetError,
  setCountryCode,
  setEnrollmentValue,
  setVerificationMethod,
  switchMfaType,
  verificationMethod,
  consents,
  onConsentChange,
}) => {
  const onSmsChange = (e) => {
    setEnrollmentValue(e.target.value);
  };

  const submitEnrollment = useCallback(() => {
    onStartVerification();
  }, [onStartVerification]);

  const shouldShowForcedMfaContext =
    phoneVerificationState === PHONE_VERIFICATION_STATES.BANNER ||
    phoneVerificationState === PHONE_VERIFICATION_STATES.MODAL ||
    phoneVerificationState === PHONE_VERIFICATION_STATES.UNSKIPPABLE;

  return (
    <>
      <ModalBody className={style['add-enrollment-modal-content']}>
        <FlexColumn gap={Gap.Space16}>
          <Text
            textType={TextType.H3}
            value={shouldShowForcedMfaContext ? messages.phoneEnrollmentFormTitleForced : messages.phoneEnrollmentFormTitle}
          />
          <Text
            textType={TextType.Paragraph}
            value={shouldShowForcedMfaContext ? messages.phoneEnrollmentFormTextForced : messages.phoneEnrollmentFormText}
          />
          <FlexColumn>
            <Text textType={TextType.Label} value={messages.phoneEnrollmentInputLabel} />
            <FlexRow className={style['phone-input-row']} gap={Gap.Space4}>
              <CountryCodeSelect selectedCode={countryCode} setCountryCode={setCountryCode} />
              <Input focusOnMount onChange={onSmsChange} placeholder='(123) 456-7890' value={enrollmentValue} version={version2} />
            </FlexRow>
          </FlexColumn>
          <Text textType={TextType.Label} value={messages.phoneEnrollmentDeliveryMethodLabel} />
          {shouldShowForcedMfaContext ? (
            <RadioCheckList>
              <RadioCheckListItem
                id={AUTH0_MFA_TYPES.sms}
                isChecked={verificationMethod === AUTH0_MFA_TYPES.sms}
                onChecked={() => setVerificationMethod(AUTH0_MFA_TYPES.sms)}
                value={AUTH0_MFA_TYPES.sms}
              >
                <FormattedMessage {...messages.smsEnrollmentOption} />
              </RadioCheckListItem>
              <RadioCheckListItem
                id={AUTH0_MFA_TYPES.voice}
                isChecked={verificationMethod === AUTH0_MFA_TYPES.voice}
                onChecked={() => setVerificationMethod(AUTH0_MFA_TYPES.voice)}
                value={AUTH0_MFA_TYPES.voice}
              >
                <FormattedMessage {...messages.voiceEnrollmentOption} />
              </RadioCheckListItem>
            </RadioCheckList>
          ) : (
            <div className={style['phone-verification-dropdown']}>
              <SimpleDropdown
                defaultTitle={<FormattedMessage {...messages.smsEnrollmentOption} />}
                items={VerificationMethods}
                onItemSelected={setVerificationMethod}
                selectedItemKey={verificationMethod}
              />
            </div>
          )}
          {shouldShowForcedMfaContext ? (
            <FlexColumn gap={Gap.Space8}>
              <Checkbox
                checked={consents?.[0]?.accepted || false}
                className={style['consent-checkbox']}
                onChange={(checked) => onConsentChange && onConsentChange('phoneConsentCustomerServiceAndSupportCheckboxLabel', checked)}
              >
                <FormattedMessage {...messages.phoneConsentCustomerServiceAndSupportCheckboxLabel} />
              </Checkbox>
              <Text className={style['consent-disclaimer']} textType={TextType.Small} value={messages.phoneConsentDisclaimer} />
            </FlexColumn>
          ) : null}
          {!!consents && !shouldShowForcedMfaContext && (
            <FlexColumn gap={Gap.Space8}>
              {consents.map((consent) => (
                <Checkbox
                  key={consent.key}
                  checked={consent.accepted}
                  className={style['consent-checkbox']}
                  onChange={(checked) => onConsentChange(consent.key, checked)}
                >
                  {typeof consent.text === 'string' ? consent.text : <FormattedMessage {...consent.text} />}
                </Checkbox>
              ))}

              <Text className={style['consent-disclaimer']} textType={TextType.Small} value={messages.phoneConsentDisclaimer} />
            </FlexColumn>
          )}
          <EnrollmentErrorBanner error={error} resetError={resetError} />
        </FlexColumn>
      </ModalBody>
      <ModalFooter>
        <FlexRow className={style.footer} gap={Gap.Space16}>
          {switchMfaType && isUnskippable ? (
            <Button
              onClick={() => switchMfaType(currentMfaType === MANAGEMENT_TYPES.PHONE ? MANAGEMENT_TYPES.TOTP : MANAGEMENT_TYPES.PHONE)}
              theme='secondary'
            >
              <FormattedMessage {...messages.authenticateAnotherWayButton} />
            </Button>
          ) : null}
          {!isUnskippable && (
            <Button onClick={onCancel} theme='tertiary'>
              <FormattedMessage {...messages.cancelButton} />
            </Button>
          )}
          <Button disabled={!enrollmentValue.length} onClick={submitEnrollment} theme='primary'>
            <FormattedMessage {...messages.sendCodeButton} />
          </Button>
        </FlexRow>
      </ModalFooter>
    </>
  );
};
